import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Grid, Checkbox, FormControlLabel, Typography, Stack } from '@mui/material'

import { useValidPassword, useValidEmail } from '../../hooks/auth/useAuthHooks'
import { Password, Email } from '../../components/auth/authComponents'
import AuthError from '../../components/auth/authError'
import AuthHeader from '../../components/auth/authHeader'
import { AuthContext } from '../../contexts/auth/authContext'
import custom from '../../styles/mui/szVariables.json'
import { analyticsPage, analyticsDomain, analyticsCta } from '../../services/constants'
import { sendClickAnalytics, sendViewAnalytics } from '../../components/common/util/util'

export default function SignIn() {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)
  const { email, setEmail, emailIsValid } = useValidEmail('')
  const [ remember, setRemember ] = useState(false)
  const { password, setPassword, passwordIsValid } = useValidPassword('')
  const [error, setError] = useState('')

  const isNotValid = !emailIsValid || email.length === 0 

  const clickAnalytics = (name: string, metadata={}) => {
    sendClickAnalytics('/signin', {
        domain: analyticsDomain.welcome,
        name,
        metadata,
      })
  }

  const signInClicked = async () => {
    try {
      setError('')
      await auth.signInWithEmail(email, password, remember)
      clickAnalytics(analyticsCta.signIn)
      navigate('/')
    } catch (err: any) {
      clickAnalytics(analyticsCta.signInError, {
        message: err.message
      })
      setError(err.message)
    }
    
  }
  function handleEnterKey(ev: any) {
    if(ev.key === "Enter" && !isNotValid) {
      signInClicked()
    }
  }
  function handleEmailFormat() {
    if(!emailIsValid) {
      setError('Email format is invalid!')
    } else {
      setError('')
    }
  }

  const forgotPasswordClicked = async () => {
    clickAnalytics(analyticsCta.forgotPassword)
    navigate('/requestcode')
  }

  const rememberMeClicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRemember(event.target.checked)
    clickAnalytics(analyticsCta.rememberMe,{ checked: event.target.checked })
  }

  useEffect(()=>{
    sendViewAnalytics('/signin', {
        domain: analyticsDomain.welcome,
        name: analyticsPage.signIn
      })
  },[])

  return (
    <AuthHeader 
          id="signin"
          title="Welcome back"
          description="Log in with the same email and password you used to set up your MySolarZero account."
          >

        <AuthError error={error} />

        <Box width="100%" mt={custom.spacing.sm}>
          <Email emailIsValid={emailIsValid} setEmail={setEmail} 
                  email={email} 
                  onKeyUp={handleEnterKey}
                  onBlur={handleEmailFormat} />
        </Box>
        <Box width="100%" mt={custom.spacing.sm}>
          <Password label="Password" passwordIsValid={passwordIsValid} 
                    setPassword={setPassword} onKeyUp={handleEnterKey} />
          <Stack alignItems="center" direction="row" 
                  justifyContent="center" mt={1}>
            <Box>
              <FormControlLabel componentsProps={{ 
                typography: { variant:'szBody.Regular.Small'}}}
                control={<Checkbox id="rememberMe" checked={remember} 
                onChange={rememberMeClicked} />} 
                label="Remember me" />
            </Box>
            <Box id="forgotPasswordLink" onClick={forgotPasswordClicked}  
                  sx={{cursor:"pointer", marginLeft: 'auto'}}>
              <Typography variant="szBody.Regular.Small" color="primary" >
                Forgot Password?
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Buttons */}
        <Box width="100%" mt={custom.spacing.sm}>
          <Grid container direction="row" justifyContent="center">
              <Button id="signIn" fullWidth={true}  
                      disabled={isNotValid}
                      sx={{ p: custom.spacing.sm }} 
                      color="primary" variant="contained"
                      onKeyUp={handleEnterKey} 
                      onClick={signInClicked}>
                Sign In
              </Button>
          </Grid>
        </Box>
   </AuthHeader>
  )
}
